import { Box } from '@mui/material';
import { Container, Heading, Section, Text } from '../components/atomic';
import SecureEmail from '../components/SecureEmail';
import { LegalPageLayout } from '../layouts';

/**
 * Cookie Policy page component for RSM Collective
 *
 * Comprehensive cookie policy explaining how RSM Collective uses cookies
 * and similar tracking technologies on their website.
 *
 * @returns {JSX.Element} Cookie Policy page component
 */
function CookiePolicy() {
  return (
    <LegalPageLayout>
      <Section
        sx={{
          backgroundColor: 'var(--color-background-light)',
          py: 0,
          position: 'relative',
        }}
      >
        {/* Green Bar Header Treatment */}
        <Box
          sx={{
            borderTop: '4px solid var(--color-primary)',
            borderBottom: '4px solid var(--color-primary)',
            backgroundColor:
              'color-mix(in srgb, var(--color-primary) 5%, transparent)',
            py: 4,
            mb: 6,
          }}
        >
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center' }}>
              <Heading
                variant="h1"
                sx={{
                  color: 'text.primary',
                  fontWeight: 600,
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  fontFamily: '"Playfair Display", serif',
                }}
              >
                Cookie Policy
              </Heading>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ pb: 8 }}>
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {/* Introduction */}
            <Box sx={{ mb: 6 }}>
              <Text sx={{ fontSize: '1.125rem', lineHeight: 1.7, mb: 3 }}>
                This Cookie Policy explains how RSM Collective ("we," "our," or
                "us") uses cookies and similar tracking technologies when you
                visit our website. This policy should be read alongside our
                Privacy Policy.
              </Text>
            </Box>

            {/* What Are Cookies */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                What Are Cookies?
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                Cookies are small text files that are placed on your computer or
                mobile device when you visit a website. They are widely used to
                make websites work more efficiently and to provide information
                to website owners.
              </Text>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                Cookies allow websites to recognize your device and remember
                information about your visit, such as your preferred language
                and other settings. This can make your next visit easier and the
                site more useful to you.
              </Text>
            </Box>

            {/* Types of Cookies */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Types of Cookies We Use
              </Heading>

              <Heading
                variant="h3"
                sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
              >
                Essential Cookies
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                These cookies are necessary for the website to function
                properly. They enable core functionality such as security,
                network management, and accessibility. You may disable these by
                changing your browser settings, but this may affect how the
                website functions.
              </Text>

              <Heading
                variant="h3"
                sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
              >
                Analytics Cookies
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                These cookies help us understand how visitors interact with our
                website by collecting and reporting information anonymously.
                This includes:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Number of visitors to our site
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Pages visited and time spent on each page
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  How visitors arrived at our site
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  What pages visitors go to next
                </Text>
              </Box>

              <Heading
                variant="h3"
                sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
              >
                Functional Cookies
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                These cookies enable the website to provide enhanced
                functionality and personalization. They may be set by us or by
                third-party providers whose services we have added to our pages.
              </Text>

              <Heading
                variant="h3"
                sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
              >
                Marketing Cookies
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                These cookies track your online activity to help advertisers
                deliver more relevant advertising or to limit how many times you
                see an ad. These cookies can share that information with other
                organizations or advertisers.
              </Text>
            </Box>

            {/* Third-Party Cookies */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Third-Party Cookies
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                Some cookies on our site are set by third-party services that
                appear on our pages. We use the following third-party services
                that may set cookies:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Google Analytics - for website analytics
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Google Tag Manager - for managing website tags
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  LinkedIn - for professional networking integration
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  YouTube - for embedded video content
                </Text>
              </Box>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                These third-party services have their own privacy and cookie
                policies, which we encourage you to review.
              </Text>
            </Box>

            {/* How to Control Cookies */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                How to Control Cookies
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                You have the right to decide whether to accept or reject
                cookies. You can exercise your cookie preferences by clicking on
                the appropriate opt-out links provided in the cookie banner when
                you visit our website.
              </Text>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                You can also set or amend your web browser controls to accept or
                refuse cookies. If you choose to reject cookies, you may still
                use our website, though your access to some functionality and
                areas may be restricted.
              </Text>

              <Heading
                variant="h3"
                sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
              >
                Browser Settings
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                Most web browsers allow you to control cookies through their
                settings preferences. Here are links to cookie settings for
                popular browsers:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Chrome: Settings → Privacy and security → Cookies and other
                  site data
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Firefox: Preferences → Privacy & Security → Cookies and Site
                  Data
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Safari: Preferences → Privacy → Cookies and website data
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Edge: Settings → Cookies and site permissions → Cookies and
                  site data
                </Text>
              </Box>
            </Box>

            {/* Cookie Retention */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Cookie Retention
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                The length of time a cookie remains on your device depends on
                whether it is a "persistent" or "session" cookie:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  <strong>Session cookies:</strong> These are temporary and are
                  deleted when you close your browser
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  <strong>Persistent cookies:</strong> These remain on your
                  device until they expire or you delete them
                </Text>
              </Box>
            </Box>

            {/* Updates to This Policy */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Updates to This Cookie Policy
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We may update this Cookie Policy from time to time to reflect
                changes in our practices or for other operational, legal, or
                regulatory reasons. We encourage you to review this page
                periodically to stay informed about our use of cookies.
              </Text>
            </Box>

            {/* Contact Information */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Contact Us
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                If you have any questions about our use of cookies or this
                Cookie Policy, please contact us at:
              </Text>
              <Text sx={{ mb: 2, lineHeight: 1.7, fontWeight: 500 }}>
                RSM Collective
                <br />
                Email:{' '}
                <SecureEmail
                  emailKey="support"
                  showAsLink={false}
                  component="span"
                />
                <br />
                Address: 401 West A St. Ste. 200, San Diego, CA 92101
              </Text>
            </Box>
          </Box>
        </Container>
      </Section>
    </LegalPageLayout>
  );
}

export default CookiePolicy;
